/**
 * UploadBoxV2 — Drag-&-Drop + Vorschau + Auto-Komprimierung für Vermittler-ID-Dokumente.
 *
 * Verbesserungen ggü. v1:
 *  - Drag-&-Drop Zone (Datei-Drop direkt aufs Card-Tile)
 *  - Bild-Vorschau-Thumbnail (klickbar → öffnet Vollbild im neuen Tab)
 *  - PDF-Erkennung mit dediziertem Icon
 *  - Automatische Komprimierung (Canvas, >800 KB → JPEG q=0.85)
 *  - Live-Anzeige Originalgröße → komprimierte Größe (zeigt Einsparung)
 *  - Inline-Foto-Tipps („Foto richtig schießen") als zuklappbarer Hint pro Slot
 *  - Upload-Progress (spinner)
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
    Upload, Image as ImageIcon, FileText, X, Loader2,
    Lightbulb, ChevronDown, ZoomIn, AlertTriangle,
} from "lucide-react";
import api from "../../lib/api";
import { compressImage } from "../../lib/imageCompression";
import { Label } from "../ui/label";

const HINTS_DEFAULT = [
    "Auf eine ebene Fläche legen (Tisch, Schreibtisch)",
    "Tageslicht oder helle Lampe — kein direkter Blitz (Spiegelung!)",
    "Alle 4 Ecken des Dokuments müssen vollständig im Bild sein",
    "Senkrecht von oben fotografieren (Smartphone parallel zum Dokument)",
    "Kein Fingerabdruck, keine Verdeckung des Texts oder MRZ-Zeile",
    "Schärfe prüfen: Text muss klar lesbar sein",
];

function fmtBytes(n) {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function UploadBoxV2({ label, url, onUploaded, onClear, testid, hints }) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [sizes, setSizes] = useState({ orig: 0, sent: 0 });
    const [tipsOpen, setTipsOpen] = useState(false);
    const [imgFailed, setImgFailed] = useState(false);
    const isPdf = !!url && /\.pdf(\?|$)/i.test(url);

    const handleFile = async (file) => {
        if (!file) return;
        const orig = file.size;
        setUploading(true);
        setSizes({ orig, sent: 0 });
        try {
            const finalFile = file.type.startsWith("image/") ? await compressImage(file) : file;
            setSizes({ orig, sent: finalFile.size });
            const fd = new FormData();
            fd.append("file", finalFile);
            const r = await api.post("/upload/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onUploaded(r.data.url);
            const savedPct = orig > 0 ? Math.max(0, Math.round((1 - finalFile.size / orig) * 100)) : 0;
            if (file.type.startsWith("image/") && savedPct >= 10) {
                toast.success(`Hochgeladen — ${savedPct}% kleiner durch Komprimierung`);
            } else {
                toast.success('Hochgeladen — Klick "Dokumente speichern" zum Übernehmen');
            }
        } catch {
            toast.error("Upload fehlgeschlagen");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    return (
        <div className="border border-gray-200 rounded-sm p-3 flex flex-col" data-testid={testid}>
            <div className="flex items-start justify-between gap-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</Label>
                <button
                    type="button"
                    onClick={() => setTipsOpen((v) => !v)}
                    className="text-[10px] uppercase tracking-wider font-bold text-[#111827] hover:text-[#FFCC00] inline-flex items-center gap-1"
                    data-testid={`${testid}-tips-toggle`}
                    aria-expanded={tipsOpen}
                >
                    <Lightbulb className="w-3 h-3" /> Tipps
                    <ChevronDown className={`w-3 h-3 transition-transform ${tipsOpen ? "rotate-180" : ""}`} />
                </button>
            </div>

            {tipsOpen && (
                <ul className="mt-2 mb-1 text-[10.5px] text-gray-600 space-y-0.5 bg-[#FFFBEB] border border-[#FFCC00]/40 rounded-sm p-2" data-testid={`${testid}-tips`}>
                    {(hints || HINTS_DEFAULT).map((tip) => (
                        <li key={tip} className="flex items-start gap-1.5">
                            <span className="text-[#FFCC00] shrink-0 mt-0.5">●</span>
                            <span>{tip}</span>
                        </li>
                    ))}
                </ul>
            )}

            {url ? (
                <div className="mt-2 relative group" data-testid={`${testid}-preview`}>
                    {isPdf ? (
                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="block bg-gray-100 border border-gray-200 rounded-sm h-32 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
                        >
                            <div className="text-center">
                                <FileText className="w-8 h-8 mx-auto mb-1" />
                                <div className="text-[11px] font-bold">PDF öffnen</div>
                            </div>
                        </a>
                    ) : imgFailed ? (
                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="block bg-amber-50 border border-amber-300 rounded-sm h-32 flex items-center justify-center text-amber-800 hover:bg-amber-100 transition-colors"
                            data-testid={`${testid}-preview-broken`}
                        >
                            <div className="text-center px-3">
                                <AlertTriangle className="w-6 h-6 mx-auto mb-1" />
                                <div className="text-[11px] font-bold">Vorschau nicht ladbar</div>
                                <div className="text-[10px] mt-0.5">Klick öffnet Originaldatei</div>
                            </div>
                        </a>
                    ) : (
                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded-sm overflow-hidden border border-gray-200 relative"
                            title="Vollbild öffnen"
                        >
                            <img
                                src={url}
                                alt={label}
                                onError={() => setImgFailed(true)}
                                className="w-full h-32 object-cover bg-gray-100 group-hover:opacity-90 transition-opacity"
                            />
                            <div className="absolute top-1 right-1 bg-black/80 text-white rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <ZoomIn className="w-3.5 h-3.5" />
                            </div>
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={onClear}
                        className="absolute top-1 left-1 bg-white/90 hover:bg-white text-red-600 rounded-sm w-6 h-6 flex items-center justify-center shadow-sm"
                        title="Entfernen"
                        data-testid={`${testid}-clear`}
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <label
                    onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`mt-2 flex-1 flex flex-col items-center justify-center gap-1 cursor-pointer min-h-[8rem] border-2 border-dashed rounded-sm text-xs transition-colors ${dragOver ? "border-[#FFCC00] bg-[#FFFBEB] text-[#111827]" : "border-gray-300 text-gray-600 hover:border-[#111827] hover:bg-gray-50"}`}
                    data-testid={`${testid}-dropzone`}
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin text-[#FFCC00]" />
                            <span className="text-[11px]">Lädt hoch…</span>
                            {sizes.sent > 0 && (
                                <span className="text-[10px] text-gray-500 font-mono-num">
                                    {fmtBytes(sizes.orig)} → {fmtBytes(sizes.sent)}
                                </span>
                            )}
                        </>
                    ) : (
                        <>
                            {dragOver ? <ImageIcon className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                            <span className="font-bold">
                                {dragOver ? "Datei hier loslassen" : "Klicken oder hier reinziehen"}
                            </span>
                            <span className="text-[10px] text-gray-500">JPG, PNG oder PDF · Auto-Komprimierung</span>
                        </>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid={`${testid}-input`}
                    />
                </label>
            )}
        </div>
    );
}
